import Accordion from '@material-ui/core/Accordion';
import Grid from '@material-ui/core/Grid';
import MaterialUILink from '@material-ui/core/Link';
import React, { useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import { ClientScoresTileAccordionContents } from './ClientScoresTileAccordionContents';
import { fetchReleasedClientsWithDashboard } from 'actions/releasedClientsWithDashboard';
import { getOptionsIfNeeded } from '@pec/aion-ui-core/actions/options';
import { GridContainer } from '@pec/aion-ui-components/components/GridContainer';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Tile } from 'components/Tile';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useTypedSelector } from 'combineReducers';
import { useParams } from 'react-router-dom';

export const scoreTileStyles = makeStyles((theme: Theme) => ({
  accordionDetails: {
    padding: `${theme.spacing()}px 0px`
  },
  chip: {
    color: 'white',
    fontWeight: 'bold',
    marginRight: theme.spacing(3)
  },
  panel: {
    borderTop: `1px solid ${theme.palette.divider}`,
    margin: `0 -${theme.spacing(1.5)}px`,
    '&:last-child': {
      marginBottom: `-${theme.spacing(1.5)}px`
    },
    '&.Mui-expanded': {
      margin: `0 -${theme.spacing(1.5)}px`
    }
  },
  firstPanel: {
    borderTop: 'none',
    '&.MuiAccordion-root:before': {
      backgroundColor: theme.palette.common.white
    }
  },
  table: {
    '& td': {
      paddingLeft: theme.spacing(3)
    },
    '& th': {
      paddingLeft: theme.spacing(3)
    },
    '& tr:first-child td': {
      borderTop: `1px solid ${theme.palette.divider}`
    }
  },
  tableCell: {
    width: '50%'
  }
}));

type RouteParams = {
  organizationId: string;
};

export const ClientScoresTileComponent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams<RouteParams>();
  const classes = scoreTileStyles();
  const { t } = useTranslation();
  const companyProfileLinkProps = { href: `/organizations/${organizationId}/profile` };
  const releasesLinkProps = { href: `/organizations/${organizationId}/profile/hiring-clients` };
  const { releasedClientsWithDashboard, error } = useTypedSelector(state => state.releasedClientsWithDashboard);
  const { clientScoresTileExpandedOrganizationIds } = useTypedSelector(state => state.options);

  useEffect(() => {
    dispatch(getOptionsIfNeeded);
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchReleasedClientsWithDashboard(organizationId));
  }, [dispatch, organizationId]);

  const clients =
    releasedClientsWithDashboard !== null
      ? releasedClientsWithDashboard?.map(r => ({
          isExpanded: clientScoresTileExpandedOrganizationIds.indexOf(r.id) > -1,
          ...r
        }))
      : [];

  return releasedClientsWithDashboard ? (
    <Tile primaryText={t('subscriber:dashboard.clientScoresTile.title', 'Client Scores')}>
      {clients && clients.length > 0 ? (
        clients.map((client, index) => (
          <Accordion
            elevation={0}
            key={client.id}
            expanded={client.isExpanded}
            className={index < 1 ? `${classes.panel} ${classes.firstPanel}` : classes.panel}
          >
            <ClientScoresTileAccordionContents client={client} />
          </Accordion>
        ))
      ) : (
        <Grid item>
          <Loading />
        </Grid>
      )}
    </Tile>
  ) : error ? (
    <Tile
      isLoading={!error}
      hasError={!!error}
      primaryText={t('subscriber:dashboard.clientScoresTile.title', 'Client Scores')}
    />
  ) : (
    <Tile primaryText={t('subscriber:dashboard.clientScoresTile.title', 'Client Scores')}>
      <GridContainer>
        <Grid item xs={12}>
          <Typography variant="h6">
            {t('subscriber:dashboard.clientScoresTile.noClientScores', 'No Client Scores are Available')}
          </Typography>
          <Typography variant="body1">
            <Trans i18nKey="subscriber:dashboard.clientScoresTile.reminder">
              Please ensure your{' '}
              <MaterialUILink underline="always" {...companyProfileLinkProps}>
                Company Profile
              </MaterialUILink>{' '}
              is completed and your{' '}
              <MaterialUILink underline="always" {...releasesLinkProps}>
                Releases
              </MaterialUILink>{' '}
              are up-to-date.
            </Trans>
          </Typography>
        </Grid>
      </GridContainer>
    </Tile>
  );
};
